/**
 * Token Interceptor — attaches portal session headers to API calls.
 * Reads token from AuthService (in-memory) — NOT cookies.
 * This avoids timing issues where cookie was just set but not readable.
 */
import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { environment } from 'environments/environment';
import { AuthService } from '../services/auth.service';
import { shouldForceLogout } from './auth-error.util';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only intercept calls to portal API
    if (!req.url.includes(environment.portalApiUrl)) {
      return next.handle(req);
    }

    // Read from in-memory AuthService (always up-to-date, no cookie delay)
    let headers = req.headers;
    if (this.authService.tokenId) {
      headers = headers.set('id', this.authService.tokenId);
    }
    if (this.authService.tokenHash) {
      headers = headers.set('hash', this.authService.tokenHash);
    }
    if (environment.portalApiKey) {
      headers = headers.set('x-api-key', environment.portalApiKey);
    }

    const cloned = req.clone({ headers });

    return next.handle(cloned).pipe(
      tap({
        error: (err: HttpErrorResponse) => {
          // Only a 401 about OUR session ends it. A 401 from an SSO launch is the
          // target app rejecting the user — the portal session is still valid, so the
          // caller shows "Could not launch X" instead of being signed out.
          if (shouldForceLogout(err.status, req.url)) {
            this.authService.logout();
            this.router.navigate(['/auth/login']);
          }
        },
      })
    );
  }
}
